// OPT-295: a tenant's named sites and the one lookup that decides an item's site everywhere.
// The Node twin is amplify/backend/function/_shared/locations/locationSites.cjs; a parity test keeps them equal.
import { normalizeLocation } from "@/utils/omsPickList";

export const SITE_KINDS = ["store", "warehouse"];
export const SITE_NAME_MAX = 40;
export const SITES_MAX = 50;
export const EMPTY_SETUP = Object.freeze({ sites: Object.freeze([]), defaultSiteId: null });

const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

function asArray(sites) {
  if (Array.isArray(sites)) return sites;
  if (typeof sites === "string") {
    try {
      const v = JSON.parse(sites);
      return Array.isArray(v) ? v : null;
    } catch (e) {
      return null;
    }
  }
  return null;
}

/** A row's (sites, defaultSiteId) -> { sites: [{id,name,kind,key}], defaultSiteId }. Never throws. */
export function parseSetup(row) {
  if (!row || typeof row !== "object") return EMPTY_SETUP;
  const list = asArray(row.sites);
  if (!list) return EMPTY_SETUP;
  const ids = new Set();
  const keys = new Set();
  const sites = [];
  for (const s of list) {
    if (!isObj(s) || typeof s.id !== "string" || !s.id) continue;
    const key = normalizeLocation(s.name);
    if (!key || !SITE_KINDS.includes(s.kind)) continue;
    if (ids.has(s.id) || keys.has(key)) continue;
    ids.add(s.id);
    keys.add(key);
    sites.push({ id: s.id, name: s.name, kind: s.kind, key });
  }
  const defaultSiteId = typeof row.defaultSiteId === "string" && ids.has(row.defaultSiteId) ? row.defaultSiteId : null;
  return { sites, defaultSiteId };
}

/** Problems that block Save, as user-facing strings; [] when valid. */
export function validateSites(sites, defaultSiteId) {
  const list = Array.isArray(sites) ? sites : [];
  const out = [];
  if (list.some((s) => !s || !normalizeLocation(s.name))) out.push("Every site needs a name.");
  const seen = new Map();
  const dupes = [];
  for (const s of list) {
    const key = s && normalizeLocation(s.name);
    if (!key) continue;
    if (seen.has(key)) {
      if (!dupes.includes(key)) dupes.push(key);
    } else seen.set(key, String(s.name).trim());
  }
  for (const k of dupes) out.push(`Two sites are both called “${seen.get(k)}”.`);
  if (list.some((s) => s && typeof s.name === "string" && s.name.trim().length > SITE_NAME_MAX)) {
    out.push(`Names can be at most ${SITE_NAME_MAX} characters.`);
  }
  if (list.length > SITES_MAX) out.push(`At most ${SITES_MAX} sites.`);
  if (list.length && !list.some((s) => s && s.id === defaultSiteId)) out.push("Choose a default site.");
  return out;
}

const BARE_STORE = Object.freeze({ id: null, name: "Store", kind: "store" });

/** The site a location resolves to, or null. */
export function siteFor(location, setup) {
  const label = normalizeLocation(location);
  const sites = (setup && setup.sites) || [];
  if (!label || sites.length === 0) return null;
  const exact = sites.find((s) => s.key === label);
  if (exact) return exact;
  const m = /^IN[- ]?STORE (.+)$/.exec(label);
  if (m) {
    const store = sites.find((s) => s.kind === "store" && s.key === m[1]);
    if (store) return store;
  }
  if (/^IN[- ]?STORE$/.test(label)) return BARE_STORE;
  return sites.find((s) => s.id === setup.defaultSiteId) || null;
}

/** { kind, label } for a badge, or null when nothing should be shown. */
export function siteBadge(location, setup) {
  const site = siteFor(location, setup);
  return site ? { kind: site.kind, label: site.name } : null;
}

/** Site names for the inventory form, in list order. */
export function siteNames(setup) {
  return ((setup && setup.sites) || []).map((s) => s.name);
}
